// DeviceGrantsPanel — granulare User<->Device-Freigaben (SPEC-Device-Access-Grants).
//
// Org-Admin/Owner gibt EINZELNE Geraete fuer EINZELNE Mitglieder frei (kuratierte
// Teilmenge statt nur "eigene" vs "alle"). Beispiel: Marco darf Patrics + Marcos PC.
//
// API (alle owner/admin-only):
//   GET    /api/org/:slug/devices/:device_id/grants
//   POST   /api/org/:slug/devices/:device_id/grants   {user_sub, permission}
//   DELETE /api/org/:slug/devices/:device_id/grants/:user_sub

import { useCallback, useEffect, useState } from 'react'
import { useAuth } from '@it-sce/ui/contexts/KeycloakAuthContext'
import { displayIdentity } from '../../utils/identityDisplay'

export interface GrantMember {
  keycloak_id: string
  first_name?: string
  last_name?: string
  email: string
  role: string
}

interface Grant {
  user_sub: string
  permission: string
  granted_at?: string
}

function memberLabel(members: GrantMember[], sub: string): string {
  const m = members.find((x) => x.keycloak_id === sub)
  if (!m) return sub
  return [m.first_name, m.last_name].filter(Boolean).join(' ').trim() || displayIdentity(m)
}

export function DeviceGrantsPanel({
  orgSlug,
  deviceId,
  members,
  isAdmin,
}: {
  orgSlug: string
  deviceId: string
  members: GrantMember[]
  isAdmin: boolean
}) {
  const { api } = useAuth()
  const [grants, setGrants] = useState<Grant[]>([])
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [selUser, setSelUser] = useState('')
  const [selPerm, setSelPerm] = useState<'operate' | 'view'>('operate')

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const r = await api!.get<{ grants: Grant[] }>(
        `/api/org/${orgSlug}/devices/${deviceId}/grants`,
      )
      setGrants(r.data.grants ?? [])
      setError(null)
    } catch (e) {
      const err = e as { response?: { data?: { error?: string } } }
      setError(err.response?.data?.error ?? 'Freigaben konnten nicht geladen werden.')
    } finally {
      setLoading(false)
    }
  }, [api, orgSlug, deviceId])

  useEffect(() => {
    if (isAdmin) void load()
  }, [isAdmin, load])

  if (!isAdmin) return null

  const add = async () => {
    if (!selUser) return
    setBusy(true)
    setError(null)
    try {
      await api!.post(`/api/org/${orgSlug}/devices/${deviceId}/grants`, {
        user_sub: selUser,
        permission: selPerm,
      })
      setSelUser('')
      await load()
    } catch (e) {
      const err = e as { response?: { data?: { error?: string } } }
      setError(err.response?.data?.error ?? 'Freigabe fehlgeschlagen.')
    } finally {
      setBusy(false)
    }
  }

  const remove = async (sub: string) => {
    setBusy(true)
    setError(null)
    try {
      await api!.delete(`/api/org/${orgSlug}/devices/${deviceId}/grants/${encodeURIComponent(sub)}`)
      await load()
    } catch (e) {
      const err = e as { response?: { data?: { error?: string } } }
      setError(err.response?.data?.error ?? 'Entfernen fehlgeschlagen.')
    } finally {
      setBusy(false)
    }
  }

  // Mitglieder die noch keinen Grant haben (fuer die Auswahl).
  const grantedSubs = new Set(grants.map((g) => g.user_sub))
  const available = members.filter((m) => !grantedSubs.has(m.keycloak_id))

  return (
    <div style={{
      marginTop: '1.5rem', padding: '1rem',
      background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 8,
    }}>
      <div style={{ fontSize: '0.875rem', fontWeight: 600, color: '#1f2937', marginBottom: '0.25rem' }}>
        👥 Zugriff freigeben
      </div>
      <p style={{ fontSize: '0.8125rem', color: '#6b7280', margin: '0 0 0.75rem' }}>
        Gib dieses Gerät einzelnen Mitgliedern frei — zusätzlich zum Owner. <em>Bedienen</em>{' '}
        erlaubt Aktionen (Dienste, Updates), <em>Ansehen</em> nur Status/Performance.
      </p>

      {loading ? (
        <div style={{ fontSize: '0.8125rem', color: '#6b7280' }}>Lade Freigaben…</div>
      ) : grants.length === 0 ? (
        <div style={{ fontSize: '0.8125rem', color: '#9ca3af', marginBottom: '0.75rem' }}>
          Keine zusätzlichen Freigaben (nur Owner + Org-Admins).
        </div>
      ) : (
        <div style={{ marginBottom: '0.75rem' }}>
          {grants.map((g) => (
            <div key={g.user_sub} style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '0.4rem 0.5rem', background: '#fff',
              border: '1px solid #e5e7eb', borderRadius: 6, marginBottom: '0.375rem',
              fontSize: '0.8125rem',
            }}>
              <span>
                {memberLabel(members, g.user_sub)}{' '}
                <span style={{
                  marginLeft: 6, padding: '0.05rem 0.4rem', borderRadius: 4,
                  background: g.permission === 'operate' ? '#dbeafe' : '#f3f4f6',
                  color: g.permission === 'operate' ? '#1e40af' : '#374151', fontSize: '0.7rem',
                }}>
                  {g.permission === 'operate' ? 'Bedienen' : 'Ansehen'}
                </span>
              </span>
              <button
                onClick={() => void remove(g.user_sub)}
                disabled={busy}
                style={{
                  border: 'none', background: 'transparent', color: '#b91c1c',
                  cursor: busy ? 'wait' : 'pointer', fontSize: '0.8125rem',
                }}
              >Entfernen</button>
            </div>
          ))}
        </div>
      )}

      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
        <select
          value={selUser}
          disabled={busy || available.length === 0}
          onChange={(e) => setSelUser(e.target.value)}
          style={{
            flex: 1, padding: '0.5rem 0.625rem', borderRadius: 6,
            border: '1px solid #d1d5db', fontSize: '0.875rem', background: '#fff',
          }}
        >
          <option value="">{available.length === 0 ? 'Alle Mitglieder haben bereits Zugriff' : 'Mitglied wählen…'}</option>
          {available.map((m) => {
            const label = [m.first_name, m.last_name].filter(Boolean).join(' ').trim() || displayIdentity(m)
            return <option key={m.keycloak_id} value={m.keycloak_id}>{label} ({m.role})</option>
          })}
        </select>
        <select
          value={selPerm}
          disabled={busy}
          onChange={(e) => setSelPerm(e.target.value as 'operate' | 'view')}
          style={{
            padding: '0.5rem 0.625rem', borderRadius: 6,
            border: '1px solid #d1d5db', fontSize: '0.875rem', background: '#fff',
          }}
        >
          <option value="operate">Bedienen</option>
          <option value="view">Ansehen</option>
        </select>
        <button
          onClick={() => void add()}
          disabled={busy || !selUser}
          style={{
            padding: '0.5rem 0.875rem', borderRadius: 6, border: 'none',
            background: !selUser || busy ? '#9ca3af' : '#2563eb', color: '#fff',
            fontSize: '0.875rem', cursor: !selUser || busy ? 'not-allowed' : 'pointer',
          }}
        >Freigeben</button>
      </div>

      {error && (
        <div style={{
          marginTop: '0.5rem', padding: '0.5rem 0.75rem',
          background: '#fef2f2', border: '1px solid #fecaca',
          borderRadius: 6, color: '#b91c1c', fontSize: '0.8125rem',
        }}>{error}</div>
      )}
    </div>
  )
}
